import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useLocalizedValidationErrorMessages } from '@ai/common/src/hooks/useLocalizedValidationErrorMessages';
import ErrorPane from '@ai/common/src/pages/auth/ErrorPane';
import { NewPasswordField } from '@ai/common/src/pages/auth/NewPasswordField';
import { getUserStore, type FrontendId } from '../../store/UserStore';
import { EMAIL_REGEX_FOR_VALIDATION } from '../../constants/constants';
import { GoToLoginButton } from './GoToLoginButton';

const ResetPassword = observer(
  ({
    pathLogin,
    pathReset,
    frontendId,
  }: {
    pathLogin: string;
    pathReset: string;
    frontendId: FrontendId;
  }) => {
    const { t } = useTranslation();
    const { sendResetLink, error, setError, resetPassword } = getUserStore(frontendId);
    const [params] = useSearchParams();
    const token = params.get('token');
    const [status, setStatus] = useState<'isVerifying' | 'success' | 'error' | 'emailSent'>(
      'isVerifying'
    );
    const navigate = useNavigate();
    const width = 'min(26rem, 80vw)';

    const emailForm = useFormik({
      initialValues: { email: '' },
      validationSchema: Yup.object({
        email: Yup.string()
          .trim()
          .matches(EMAIL_REGEX_FOR_VALIDATION, t('login.error.wrongEmail'))
          .max(255, t('login.error.maxLength', { length: 255 }))
          .required(t('login.required.email')),
      }),
      onSubmit: async (data: { email: string }) => {
        try {
          const res = await sendResetLink(data.email);
          if (res) {
            setStatus('emailSent');
          }
        } catch (error) {
          console.error(error);
        }
      },
      validateOnChange: false,
      validateOnBlur: false,
    });

    useLocalizedValidationErrorMessages(emailForm);

    const newPasswordForm = useFormik({
      initialValues: { password: '', passwordConfirm: '' },
      validationSchema: Yup.object({
        password: Yup.string()
          .trim()
          .max(255, t('login.error.maxLength', { length: 255 }))
          .required(t('login.required.password')),
        passwordConfirm: Yup.string()
          .trim()
          .test('passwords-match', t('login.error.passwordMatch'), function (value) {
            return this.parent.password === value;
          }),
      }),
      onSubmit: async (data: { password: string; passwordConfirm: string }) => {
        const res = await resetPassword(token as string, data.password.trim());
        setStatus(res ? 'success' : 'error');
      },
      validateOnChange: false,
      validateOnBlur: false,
    });

    useLocalizedValidationErrorMessages(newPasswordForm);

    return (
      <>
        {!token && (
          <form noValidate onSubmit={emailForm.handleSubmit}>
            <Stack gap={2} width={width}>
              {status !== 'emailSent' && (
                <>
                  <Typography>
                    {t('login.enterEmailForReset', {
                      email: params.get('email'),
                    })}
                  </Typography>

                  <TextField
                    autoFocus
                    error={!!emailForm.errors.email}
                    fullWidth
                    helperText={emailForm.errors.email}
                    label={t('login.email')}
                    id="email"
                    type="email"
                    variant="outlined"
                    onChange={emailForm.handleChange}
                    value={emailForm.values.email}
                    disabled={emailForm.isSubmitting}
                    onFocus={() => setError(undefined)}
                  />
                </>
              )}

              {status === 'emailSent' && (
                <Typography overflow={'hidden'} textOverflow={'ellipsis'}>
                  {t('login.resetLinkSent', {
                    email: emailForm.values.email,
                  })}
                </Typography>
              )}

              <ErrorPane error={error} />

              <Button
                type="submit"
                variant="contained"
                disabled={emailForm.isSubmitting}
                startIcon={emailForm.isSubmitting ? <CircularProgress size={20} /> : ''}
              >
                {status === 'emailSent' ? t('login.resendLink') : t('login.sendLink')}
              </Button>

              <GoToLoginButton path={pathLogin} />
            </Stack>
          </form>
        )}

        {!!token && (
          <form noValidate onSubmit={newPasswordForm.handleSubmit}>
            <Stack gap={2} width={width}>
              {status === 'isVerifying' && (
                <>
                  <Typography>{t('login.createNewPassword')}</Typography>

                  <NewPasswordField
                    autoFocus
                    id="password"
                    value={newPasswordForm.values.password}
                    onChange={newPasswordForm.handleChange}
                    error={!!newPasswordForm.errors.password}
                    helperText={newPasswordForm.errors.password}
                    disabled={newPasswordForm.isSubmitting}
                    label={t('login.password')}
                  />

                  <NewPasswordField
                    id="passwordConfirm"
                    value={newPasswordForm.values.passwordConfirm}
                    onChange={newPasswordForm.handleChange}
                    error={!!newPasswordForm.errors.passwordConfirm}
                    helperText={newPasswordForm.errors.passwordConfirm}
                    disabled={newPasswordForm.isSubmitting}
                    label={t('login.passwordConfirm')}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={newPasswordForm.isSubmitting}
                    startIcon={newPasswordForm.isSubmitting ? <CircularProgress size={20} /> : ''}
                  >
                    {t('continue')}
                  </Button>
                </>
              )}

              {status === 'success' && (
                <>
                  <Typography>{t('login.passwordResetSuccess')}</Typography>
                  <Button onClick={() => navigate(pathLogin)} variant="contained">
                    {t('continue')}
                  </Button>
                </>
              )}

              {status === 'error' && (
                <>
                  <Typography>{t('login.linkError')}</Typography>
                  <Button
                    onClick={() => {
                      setError(undefined);
                      navigate(pathReset);
                    }}
                    variant="contained"
                  >
                    {t('login.resendLink')}
                  </Button>
                </>
              )}

              {status !== 'success' && <GoToLoginButton path={pathLogin} />}

              <ErrorPane error={error} />
            </Stack>
          </form>
        )}
      </>
    );
  }
);

export default ResetPassword;
